import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { SaasTenantPicker } from './SaasTenantPicker.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

interface UsageRow {
  readonly usageId: string;
  readonly saasTenantId: string;
  readonly metricCode: string;
  readonly quantity: string;
  readonly limitValue: string | null;
  readonly breached: boolean;
  readonly recordedAt: string;
}

const METRICS = ['USERS', 'STORAGE_MB', 'API_CALLS', 'WORKFLOWS', 'DOCUMENTS', 'ASSETS', 'AI_UNITS', 'PORTAL_SESSIONS'];
const PAGE = 25;

/**
 * M43 §27 Usage Monitoring Dashboard (§12 / §17 Usage & Limit Management).
 * Operators record usage samples against a tenant's metrics and see which
 * samples breached the subscription ceiling. Viewing needs USAGE_VIEW; recording
 * needs USAGE_MANAGE.
 */
export function UsageMonitoringDashboard(): ReactNode {
  const { token } = useAuth();
  const { decimal } = useBusinessNumber();
  const [rows, setRows] = useState<UsageRow[]>([]);
  const [total, setTotal] = useState(0);
  const [metricFilter, setMetricFilter] = useState('');
  const [breachedOnly, setBreachedOnly] = useState(false);
  const [page, setPage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [saasTenantId, setSaasTenantId] = useState('');
  const [metricCode, setMetricCode] = useState('USERS');
  const [quantity, setQuantity] = useState('1');
  const [enforce, setEnforce] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (metricFilter) params.set('metricCode', metricFilter);
    if (breachedOnly) params.set('breachedOnly', 'true');
    params.set('pageSize', String(PAGE));
    params.set('page', String(page + 1));
    apiRequest<{ data: UsageRow[]; total: number }>(`/v1/saas/usage?${params.toString()}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, metricFilter, breachedOnly, page, reload]);

  const record = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!saasTenantId.trim()) { setActionError('A tenant id is required.'); return; }
    const qty = Number(quantity);
    if (!Number.isFinite(qty) || qty <= 0) { setActionError('Quantity must be a positive number.'); return; }
    try {
      await apiRequest('/v1/saas/usage', {
        method: 'POST',
        body: { saasTenantId: saasTenantId.trim(), metricCode, quantity: qty, enforce },
        token,
      });
      setNotice('Usage recorded.');
      setPage(0);
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) setActionError('You are not authorised to record usage (USAGE_MANAGE required).');
      else if (err instanceof ApiError && err.status === 422) setActionError('Usage limit exceeded — the enforced ceiling was reached.');
      else setActionError((err as Error).message);
    }
  };

  return (
    <main aria-label="usage-monitoring-dashboard">
      <h1>Usage Monitoring Dashboard</h1>

      <section aria-label="usage-record">
        <h2>Record Usage</h2>
        <form aria-label="usage-form" onSubmit={(e) => void record(e)}>
          <SaasTenantPicker label="Tenant" value={saasTenantId} onPick={setSaasTenantId} />
          <label>Metric
            <select aria-label="field-metric" value={metricCode} onChange={(e) => setMetricCode(e.target.value)}>
              {METRICS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </label>
          <label>Quantity<input aria-label="field-quantity" value={quantity} onChange={(e) => setQuantity(e.target.value)} /></label>
          <label><input type="checkbox" aria-label="field-enforce" checked={enforce} onChange={(e) => setEnforce(e.target.checked)} />Enforce ceiling</label>
          <button type="submit" aria-label="submit-usage">Record</button>
        </form>
        {actionError && <p role="alert" aria-label="usage-action-error">{actionError}</p>}
        {notice && <p aria-label="usage-notice">{notice}</p>}
      </section>

      <section aria-label="usage-filters">
        <label>Metric
          <select aria-label="filter-metric" value={metricFilter} onChange={(e) => { setMetricFilter(e.target.value); setPage(0); }}>
            <option value="">All</option>
            {METRICS.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </label>
        <label><input type="checkbox" aria-label="filter-breached" checked={breachedOnly} onChange={(e) => { setBreachedOnly(e.target.checked); setPage(0); }} />Breached only</label>
      </section>

      {error && <p role="alert" aria-label="usage-error">{error}</p>}
      {loading ? <p>Loading…</p> : (
        <>
          <p aria-label="usage-total">{total} sample(s).</p>
          <table aria-label="usage-table">
            <thead><tr><th scope="col">Metric</th><th scope="col">Quantity</th><th scope="col">Limit</th><th scope="col">Breached</th></tr></thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={4}>No usage samples.</td></tr>
              ) : rows.map((u) => (
                <tr key={u.usageId} data-usage={u.usageId}>
                  <td aria-label={`usage-metric-${u.usageId}`}>{u.metricCode}</td>
                  <td>{decimal(u.quantity)}</td>
                  <td>{u.limitValue === null ? '—' : decimal(u.limitValue)}</td>
                  <td aria-label={`usage-breached-${u.usageId}`}>{u.breached ? 'Yes' : 'No'}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <nav aria-label="usage-pagination">
            <button type="button" aria-label="prev-page" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>Previous</button>
            <span aria-label="page-indicator">Page {page + 1} of {Math.max(1, Math.ceil(total / PAGE))}</span>
            <button type="button" aria-label="next-page" disabled={(page + 1) * PAGE >= total} onClick={() => setPage((p) => p + 1)}>Next</button>
          </nav>
        </>
      )}
    </main>
  );
}
